import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "../axios";

const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const [rows, setRows] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [orgName, setOrgName] = useState("");
  const [loading, setLoading] = useState(true);
  const [maxLevel, setMaxLevel] = useState(0); // State to track the highest level

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/group_details`, {
          headers: {
            accessToken: localStorage.accessToken,
          },
        });

        // Recursive function to calculate doctor count for all descendants
        const getDoctorCount = (nodes) => {
          return nodes.reduce((count, child) => {
            if (child.userId) {
              // Increment count if userId exists
              return count + 1;
            } else if (child.children && child.children.length > 0) {
              // Recurse into children
              return count + getDoctorCount(child.children);
            }
            return count;
          }, 0);
        };

        // Recursive function to process nodes and track max level
        let highestLevel = 0; // Variable to track the highest level

        const processNodes = (nodes, level = 0, parentId = "") => {
          highestLevel = Math.max(highestLevel, level); // Update highest level encountered

          return nodes.map((node, nodeIndex) => {
            const patientCount =
              node.total_patient_count || node.total_patients || 0;
            const assessmentCount =
              node.total_assessment_count || node.total_assessments || 0;

            // Construct a unique key with parentId, level, and nodeIndex
            const uniqueKey = `${parentId}-${level}-${nodeIndex}`;

            // Separate child nodes into doctors and other nodes
            const doctorNodes = (node.children || []).filter(
              (child) => child.userId
            );
            const otherNodes = (node.children || []).filter(
              (child) => !child.userId
            );

            // Process doctor nodes separately
            const doctorChildren = doctorNodes.map((doctor, doctorIndex) => ({
              key: `${uniqueKey}-doctor-${doctorIndex}`,
              data: {
                cityName: doctor.name || doctor.userId || "Unknown Doctor",
                patientCount: doctor.total_patient_count || 0,
                assessmentCount: doctor.total_assessment_count || 0,
                isDoctor: true,
              },
              children: [],
            }));

            // Process other nodes recursively
            const otherChildren = processNodes(
              otherNodes,
              level + 1,
              uniqueKey
            );

            // Combine doctor nodes and other children
            const children = [...doctorChildren, ...otherChildren];

            return {
              key: uniqueKey,
              data: {
                cityName: node.name || node.userId || "Unknown",
                patientCount,
                assessmentCount,
                doctorCount: getDoctorCount(node.children || []), // Calculate doctor count
              },
              children,
            };
          });
        };

        // Recursive function to get last-level children (leaf nodes)
        const getLastLevelChildren = (nodes) => {
          return nodes.flatMap((node) => {
            if (node.userId) {
              // If the node has a userId key, include it
              return [node];
            } else if (node.children && node.children.length > 0) {
              // Traverse further if 'children' key exists with children
              return getLastLevelChildren(node.children);
            } else {
              // Exclude nodes without userId and without children
              return [];
            }
          });
        };

        const mappedNodes = processNodes(response.data.data);
        setOrgName(response.data.org_name);

        // Set the highest level encountered
        setMaxLevel(highestLevel);
        // Extract last-level children (doctors)
        const lastLevelDoctors = getLastLevelChildren(response.data.data);

        // Update state with processed data
        setDoctors(lastLevelDoctors);
        setRows(mappedNodes);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <DataContext.Provider value={{ rows, doctors, orgName, loading, maxLevel }}>
      {children}
    </DataContext.Provider>
  );
};

// Hook to use the context
export const useData = () => useContext(DataContext);
